import { QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from "react-router";
import { Toaster } from "sonner";

import { createQueryClient } from "@/core/api/query-client";
import { createIdentity } from "@/core/auth/create-identity";
import type { IdentityAdapter } from "@/core/auth/identity";
import { SessionProvider } from "@/core/auth/session";
import { ThemeProvider, useTheme } from "@/core/theme/theme";
import { RequireAdmin } from "@/features/auth/RequireAdmin";
import { SignInPage } from "@/features/auth/SignInPage";
import { OverviewPage } from "@/features/overview/OverviewPage";
import { AppShell } from "@/layouts/AppShell";

import { PAGES } from "./pages";
import { RouteError, RouteLoading } from "./RouteError";
import { ROUTE_PATTERNS, ROUTES } from "./routes";

const routes: RouteObject[] = [
  { path: ROUTES.signIn, element: <SignInPage />, errorElement: <RouteError fullPage /> },
  {
    hydrateFallbackElement: <RouteLoading />,
    // Anything the screens below don't catch themselves: never React Router's developer page.
    errorElement: <RouteError fullPage />,
    element: (
      <RequireAdmin>
        <AppShell />
      </RequireAdmin>
    ),
    children: [
      // The landing screen ships with the app; every other screen downloads the first time it's opened.
      { index: true, element: <OverviewPage /> },
      {
        path: ROUTES.brands,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.brands()).BrandsPage }),
      },
      {
        path: ROUTE_PATTERNS.brand,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.brandDetail()).BrandDetailPage,
        }),
      },
      {
        path: ROUTES.users,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.users()).UsersPage }),
      },
      {
        path: ROUTE_PATTERNS.user,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.userDetail()).UserDetailPage }),
      },
      {
        path: ROUTES.subscriptions,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.subscriptions()).SubscriptionsPage,
        }),
      },
      {
        path: ROUTE_PATTERNS.newFreePlan,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.newFreePlan()).NewFreePlanPage,
        }),
      },
      {
        path: ROUTE_PATTERNS.subscription,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.subscriptionDetail()).SubscriptionDetailPage,
        }),
      },
      {
        path: ROUTES.disputes,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.disputes()).DisputesPage }),
      },
      {
        path: ROUTES.plans,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.plans()).PlansPage }),
      },
      {
        path: ROUTES.reviews,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.reviews()).ReviewsPage }),
      },
      {
        path: ROUTES.payments,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.payments()).PaymentsPage }),
      },
      {
        path: ROUTE_PATTERNS.payment,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.paymentDetail()).PaymentDetailPage,
        }),
      },
      {
        path: ROUTES.payouts,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.payouts()).PayoutsPage }),
      },
      {
        path: ROUTE_PATTERNS.payout,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.payoutDetail()).PayoutDetailPage,
        }),
      },
      {
        path: ROUTES.ledger,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.ledger()).LedgerPage }),
      },
      {
        path: ROUTES.reconciliation,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.reconciliation()).ReconciliationPage,
        }),
      },
      {
        path: ROUTES.promoCodes,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.promoCodes()).PromoCodesPage,
        }),
      },
      {
        path: ROUTES.dispatchTemplates,
        errorElement: <RouteError />,
        lazy: async () => ({
          Component: (await PAGES.dispatchMessages()).DispatchMessagesPage,
        }),
      },
      {
        path: ROUTES.settings,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.settings()).SettingsPage }),
      },
      {
        path: ROUTES.auditLog,
        errorElement: <RouteError />,
        lazy: async () => ({ Component: (await PAGES.auditLog()).AuditLogPage }),
      },
    ],
  },
  { path: "*", element: <Navigate to={ROUTES.overview} replace /> },
];

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} position="top-center" richColors closeButton />;
}

export function App({ identity }: { identity?: IdentityAdapter }) {
  const [queryClient] = useState(createQueryClient);
  const [identityAdapter] = useState(() => identity ?? createIdentity());
  // Made per app (not at import), so it starts from the address the app opens on (tests rely on this too).
  const [router] = useState(() => createBrowserRouter(routes));
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider identity={identityAdapter}>
          <RouterProvider router={router} />
          <ThemedToaster />
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
